import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Minus, Plus } from 'lucide-react';
import { useCopy } from '@/i18n';
import { Section } from './Section';
import { hasSplitTitle, renderSplitTitle, type SplitTitleCopy } from './titleCopy';

type FAQItem = { q: string; a: string };
type FAQCategory = { id: string; label: string; items: FAQItem[] };

type SectionCopy = SplitTitleCopy & {
  eyebrow?: string;
  lead?: string;
};

function isFAQItem(item: unknown): item is FAQItem {
  return (
    Boolean(item) &&
    typeof item === 'object' &&
    typeof (item as { q?: unknown }).q === 'string' &&
    typeof (item as { a?: unknown }).a === 'string'
  );
}

function isFAQCategory(value: unknown): value is FAQCategory {
  if (!value || typeof value !== 'object') return false;
  const cat = value as { id?: unknown; label?: unknown; items?: unknown };
  return (
    typeof cat.id === 'string' &&
    typeof cat.label === 'string' &&
    Array.isArray(cat.items) &&
    cat.items.length > 0 &&
    cat.items.every(isFAQItem)
  );
}

export function FAQSection() {
  const copy = useCopy();
  const section = copy<SectionCopy>('faq_section');
  const categoriesValue = copy<unknown>('faq_categories');
  const flatValue = copy<unknown>('faq');
  const reduceMotion = useReducedMotion() ?? false;

  const categories: FAQCategory[] = Array.isArray(categoriesValue)
    ? categoriesValue.filter(isFAQCategory)
    : [];
  // Fallback for locales without categorized copy yet: one flat accordion,
  // matching the previous behavior.
  const flatItems: FAQItem[] = Array.isArray(flatValue) ? flatValue.filter(isFAQItem) : [];

  const [activeCategory, setActiveCategory] = useState(0);
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  const hasCategories = categories.length > 0;
  const current = hasCategories
    ? categories[Math.min(activeCategory, categories.length - 1)]
    : null;
  const items = current ? current.items : flatItems;

  const handleCategoryChange = (index: number) => {
    setActiveCategory(index);
    setOpenIdx(0);
  };

  return (
    <Section id="faq" bg="paper-2">
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        {section?.eyebrow ? <span className="eyebrow">{section.eyebrow}</span> : null}
        {hasSplitTitle(section) ? (
          <h2 className="h-section" style={{ marginTop: 14 }}>
            {renderSplitTitle(section, (accent) => (
              <span className="accent">{accent}</span>
            ))}
          </h2>
        ) : null}
        {section?.lead ? (
          <p className="lead" style={{ maxWidth: '52ch', margin: '18px auto 0' }}>
            {section.lead}
          </p>
        ) : null}
      </div>

      {hasCategories ? (
        <div className="mb-10 flex flex-wrap items-center justify-center gap-2">
          {categories.map((category, index) => {
            const isActive = activeCategory === index;
            return (
              <motion.button
                key={category.id}
                type="button"
                aria-pressed={isActive}
                onClick={() => handleCategoryChange(index)}
                initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: reduceMotion ? 0 : 0.4, delay: index * 0.06 }}
                className="px-5 py-2.5 text-sm font-medium transition-transform"
                style={{
                  color: 'var(--ink)',
                  background: isActive ? 'var(--sand)' : 'transparent',
                  border: isActive ? '2px dashed var(--ink)' : '1px solid var(--line-strong)',
                  borderRadius: isActive ? 999 : 8,
                  transform: isActive && !reduceMotion ? 'rotate(-2deg)' : undefined,
                }}
              >
                {category.label}
              </motion.button>
            );
          })}
        </div>
      ) : null}

      <div
        className={
          hasCategories
            ? 'mx-auto grid w-full max-w-[980px] grid-cols-1 gap-8 lg:grid-cols-[1fr_2fr]'
            : 'mx-auto w-full max-w-[720px]'
        }
      >
        {hasCategories && current ? (
          <div className="lg:sticky lg:top-24 lg:self-start">
            <motion.h3
              key={current.id}
              initial={reduceMotion ? false : { opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.35 }}
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(26px, 3vw, 38px)',
                color: 'var(--ink)',
              }}
            >
              {current.label}
            </motion.h3>
          </div>
        ) : null}

        <div key={current?.id ?? 'flat'}>
          {items.map((item, index) => (
            <div key={index} style={{ borderBottom: '1px solid var(--line)' }}>
              <button
                type="button"
                onClick={() => setOpenIdx(openIdx === index ? null : index)}
                aria-expanded={openIdx === index}
                className="group flex w-full items-start justify-between gap-4 py-5 text-start sm:py-6"
              >
                <span
                  className="flex-1 transition-opacity group-hover:opacity-70"
                  style={{ fontSize: 17, fontWeight: 500, color: 'var(--ink)' }}
                >
                  {item.q}
                </span>
                <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center">
                  {openIdx === index ? (
                    <Minus className="h-5 w-5" style={{ color: 'var(--coral)' }} />
                  ) : (
                    <Plus className="h-5 w-5" style={{ color: 'var(--ink)' }} />
                  )}
                </span>
              </button>
              <AnimatePresence initial={false}>
                {openIdx === index && (
                  <motion.div
                    initial={reduceMotion ? false : { height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={reduceMotion ? undefined : { height: 0, opacity: 0 }}
                    transition={{
                      height: { duration: reduceMotion ? 0 : 0.3, ease: [0.4, 0, 0.2, 1] },
                      opacity: { duration: reduceMotion ? 0 : 0.2, ease: 'easeInOut' },
                    }}
                    className="overflow-hidden"
                  >
                    <p
                      className="pb-5 pe-8 sm:pb-6"
                      style={{ fontSize: 15, lineHeight: 1.65, color: 'var(--muted)' }}
                    >
                      {item.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}
